<script setup>
import { computed, inject, onMounted, reactive, ref } from 'vue'
import { ArrowDown, ArrowUp, Pencil, Plus } from '@lucide/vue'
import { Badge, Button, ConfirmAction, EmptyState, FormField, Input, Modal, PageHeader, Section, Select, Spinner, Table, Textarea, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { createHomepageCarouselItem, deleteHomepageCarouselItem, listHomepageCarouselItems, reorderHomepageCarouselItems, updateHomepageCarouselItem } from '../api/homepage-carousel.js'

const { t } = useI18n()
const toast = useToast()
const api = inject('api')
if (!api) throw new Error('HomepageCarousel: api provider is required')
const items = ref([])
const packs = ref([])
const loading = ref(true)
const error = ref('')
const formOpen = ref(false)
const editingId = ref('')
const saving = ref(false)
const form = reactive({ item_type: 'youtube', reference_id: '', eyebrow: '', title: '', summary: '', enabled: true })
const typeOptions = computed(() => [
  { value: 'youtube', label: t('visualGameAdmin.homepageCarousel.types.youtube') },
  { value: 'story', label: t('visualGameAdmin.homepageCarousel.types.story') },
])
const storyOptions = computed(() => {
  const options = new Map()
  for (const pack of packs.value) {
    const value = pack.series_id ? `series:${pack.series_id}` : `scenario:${pack.id}`
    const label = pack.series_id ? (pack.series_title || pack.title) : pack.title
    if (!options.has(value)) options.set(value, { value, label: `${label} · ${value}` })
  }
  if (form.item_type === 'story' && form.reference_id && !options.has(form.reference_id)) {
    options.set(form.reference_id, { value: form.reference_id, label: form.reference_id })
  }
  return [...options.values()].sort((left, right) => left.label.localeCompare(right.label))
})
const columns = computed(() => [
  { key: 'sort_order', label: t('visualGameAdmin.homepageCarousel.order'), width: '130px' },
  { key: 'item_type', label: t('visualGameAdmin.homepageCarousel.type'), width: '110px' },
  { key: 'content', label: t('visualGameAdmin.homepageCarousel.content'), width: '520px', wrap: true },
  { key: 'enabled', label: t('visualGameAdmin.homepageCarousel.status'), width: '100px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '270px', align: 'right' },
])

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [carouselResult, packResult] = await Promise.all([
      listHomepageCarouselItems(),
      api.visualGame.listPacks(),
    ])
    items.value = carouselResult.items
    packs.value = packResult.packs
  }
  catch (requestError) { error.value = requestError.message }
  finally { loading.value = false }
}

function openCreate() {
  editingId.value = ''
  Object.assign(form, { item_type: 'youtube', reference_id: '', eyebrow: '', title: '', summary: '', enabled: true })
  formOpen.value = true
}

function openEdit(item) {
  editingId.value = item.id
  Object.assign(form, { item_type: item.item_type, reference_id: item.reference_id, eyebrow: item.eyebrow || '', title: item.title || '', summary: item.summary || '', enabled: item.enabled })
  formOpen.value = true
}

function setItemType(itemType) {
  form.item_type = itemType
  form.reference_id = ''
}

function storyTitle(referenceId) {
  if (referenceId.startsWith('series:')) {
    const seriesId = referenceId.slice('series:'.length)
    const pack = packs.value.find(item => item.series_id === seriesId)
    return pack?.series_title || pack?.title || (seriesId === 'otome_castle' ? t('visualGameAdmin.homepageCarousel.defaultStoryTitles.otomeCastle') : '')
  }
  if (referenceId.startsWith('scenario:')) {
    return packs.value.find(item => item.id === referenceId.slice('scenario:'.length))?.title || ''
  }
  return ''
}

function itemTitle(item) {
  return item.title || (item.item_type === 'story' ? storyTitle(item.reference_id) : '') || item.reference_id
}

function payload() {
  return { ...form, reference_id: form.reference_id.trim(), eyebrow: form.eyebrow.trim() || null, title: form.title.trim() || null, summary: form.summary.trim() || null }
}

async function save() {
  saving.value = true
  try {
    if (editingId.value) await updateHomepageCarouselItem(editingId.value, payload())
    else await createHomepageCarouselItem(payload())
    formOpen.value = false
    await load()
    toast.success(t('visualGameAdmin.homepageCarousel.saved'))
  } catch (requestError) { toast.error(requestError.message, { duration: 0 }) }
  finally { saving.value = false }
}

async function toggle(item) {
  await updateHomepageCarouselItem(item.id, { item_type: item.item_type, reference_id: item.reference_id, eyebrow: item.eyebrow, title: item.title, summary: item.summary, enabled: !item.enabled })
  await load()
}

async function move(index, offset) {
  const next = [...items.value]
  const [item] = next.splice(index, 1)
  next.splice(index + offset, 0, item)
  items.value = (await reorderHomepageCarouselItems(next.map(entry => entry.id))).items
}

async function remove(item) { await deleteHomepageCarouselItem(item.id) }
async function onDeleted() { await load(); toast.success(t('visualGameAdmin.homepageCarousel.deleted')) }
const valid = computed(() => form.reference_id.trim() !== '')
</script>

<template>
  <div class="carousel-page vga-page">
    <PageHeader :title="t('visualGameAdmin.homepageCarousel.title')" :subtitle="t('visualGameAdmin.homepageCarousel.subtitle')">
      <template #actions><Button @click="openCreate"><Plus :size="16" />{{ t('visualGameAdmin.homepageCarousel.add') }}</Button></template>
    </PageHeader>
    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.common.loading') }}</span></div>
    <div v-else-if="error" class="vga-state"><p class="vga-error">{{ error }}</p><Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button></div>
    <Section v-else :padded="false">
      <EmptyState v-if="items.length === 0" :title="t('visualGameAdmin.homepageCarousel.empty')" :description="t('visualGameAdmin.homepageCarousel.emptyDescription')" />
      <Table v-else :columns="columns" :rows="items">
        <template #sort_order="{ row }">
          <div class="carousel-page__order"><Button variant="ghost" size="sm" :disabled="items.indexOf(row) === 0" @click="move(items.indexOf(row), -1)"><ArrowUp :size="14" /></Button><Button variant="ghost" size="sm" :disabled="items.indexOf(row) === items.length - 1" @click="move(items.indexOf(row), 1)"><ArrowDown :size="14" /></Button></div>
        </template>
        <template #item_type="{ row }"><Badge size="sm">{{ t(`visualGameAdmin.homepageCarousel.types.${row.item_type}`) }}</Badge></template>
        <template #content="{ row }"><div class="carousel-page__content"><strong>{{ itemTitle(row) }}</strong><small>{{ row.reference_id }}</small><span v-if="row.summary">{{ row.summary }}</span></div></template>
        <template #enabled="{ row }"><Badge size="sm" :variant="row.enabled ? 'success' : 'neutral'">{{ t(row.enabled ? 'visualGameAdmin.homepageCarousel.enabled' : 'visualGameAdmin.homepageCarousel.disabled') }}</Badge></template>
        <template #actions="{ row }"><div class="carousel-page__actions"><Button variant="ghost" size="sm" @click="toggle(row)">{{ t(row.enabled ? 'visualGameAdmin.homepageCarousel.disable' : 'visualGameAdmin.homepageCarousel.enable') }}</Button><Button variant="ghost" size="sm" @click="openEdit(row)"><Pencil :size="14" />{{ t('visualGameAdmin.common.edit') }}</Button><ConfirmAction :label="t('visualGameAdmin.common.delete')" :confirm-text="t('visualGameAdmin.homepageCarousel.deleteConfirm')" :cancel-label="t('visualGameAdmin.common.cancel')" :ok-label="t('visualGameAdmin.common.delete')" :pending-label="t('visualGameAdmin.common.working')" danger :action="() => remove(row)" @done="onDeleted" @error="requestError => toast.error(requestError.message, { duration: 0 })" /></div></template>
      </Table>
    </Section>
    <Modal :open="formOpen" :title="t(editingId ? 'visualGameAdmin.homepageCarousel.editTitle' : 'visualGameAdmin.homepageCarousel.createTitle')" width="600px" :close-on-backdrop="!saving" @update:open="formOpen = $event">
      <form class="carousel-page__form" @submit.prevent="save">
        <FormField :label="t('visualGameAdmin.homepageCarousel.type')" required><Select :model-value="form.item_type" :options="typeOptions" block @update:model-value="setItemType" /></FormField>
        <FormField v-if="form.item_type === 'story'" :label="t('visualGameAdmin.homepageCarousel.story')" :hint="t('visualGameAdmin.homepageCarousel.storyHint')" required>
          <Select v-model="form.reference_id" :options="storyOptions" :placeholder="t('visualGameAdmin.homepageCarousel.storyPlaceholder')" :search-placeholder="t('visualGameAdmin.homepageCarousel.storySearchPlaceholder')" :empty-text="t('visualGameAdmin.homepageCarousel.storySearchEmpty')" searchable block />
        </FormField>
        <FormField v-else :label="t('visualGameAdmin.homepageCarousel.referenceId')" :hint="t('visualGameAdmin.homepageCarousel.youtubeHint')" required><Input v-model="form.reference_id" block /></FormField>
        <FormField :label="t('visualGameAdmin.homepageCarousel.eyebrow')"><Input v-model="form.eyebrow" block /></FormField>
        <FormField :label="t('visualGameAdmin.homepageCarousel.itemTitle')"><Input v-model="form.title" block /></FormField>
        <FormField :label="t('visualGameAdmin.homepageCarousel.summary')"><Textarea v-model="form.summary" :rows="3" block /></FormField>
        <label class="carousel-page__check"><input v-model="form.enabled" type="checkbox" />{{ t('visualGameAdmin.homepageCarousel.enabled') }}</label>
      </form>
      <template #footer><Button variant="ghost" :disabled="saving" @click="formOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button><Button :loading="saving" :disabled="!valid" @click="save">{{ t('visualGameAdmin.common.save') }}</Button></template>
    </Modal>
  </div>
</template>

<style scoped>
.carousel-page__order,.carousel-page__actions{display:flex;align-items:center;gap:var(--drama-space-xs)}.carousel-page__content{display:grid;gap:var(--drama-space-2xs)}.carousel-page__content small,.carousel-page__content span{color:var(--drama-color-text-muted)}.carousel-page__form{display:grid;gap:var(--drama-space-md)}.carousel-page__check{display:flex;align-items:center;gap:var(--drama-space-xs)}
</style>
